// src/views/BondsView.js
import React, { useState, useEffect } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Landmark, Info, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { API_BASE } from '../api/db';

export default function BondsView({ setCurrentRoute, setGlobalSearchTicker, themeClasses }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const triggerDeepDive = (ticker) => {
    setGlobalSearchTicker(ticker);
    setCurrentRoute('dashboard');
  };

  useEffect(() => {
    const baseUrl = `${window.location.origin}${API_BASE}`;

    fetch(`${baseUrl}/model/yields`)
      .then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || "Backend or API Error");
        return data;
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
           setRates(data.filter(d => d.rate > 0));
        } else {
           throw new Error("No yield curve data available.");
        }
        setLoading(false);
      })
      .catch((e) => {
        console.warn("Yield Fetch Failed:", e);
        setErrorMsg(e.message);
        setRates([]);
        setLoading(false);
      });
  }, []);

  const [bondETFs] = useState([
    { ticker: 'SHY', name: 'iShares 1-3 Year Treasury Bond ETF', category: 'Short Treasuries', yield: '4.62%' },
    { ticker: 'IEF', name: 'iShares 7-10 Year Treasury Bond ETF', category: 'Intermediate Treasuries', yield: '4.15%' },
    { ticker: 'TLT', name: 'iShares 20+ Year Treasury Bond ETF', category: 'Long Treasuries', yield: '4.48%' },
    { ticker: 'VTIP', name: 'Vanguard Short-Term Inflation-Protected', category: 'TIPS', yield: '3.20%' },
    { ticker: 'LQD', name: 'iShares iBoxx $ Investment Grade Corporate', category: 'Corp Grade', yield: '5.20%' },
    { ticker: 'HYG', name: 'iShares iBoxx $ High Yield Corporate Bond', category: 'High Yield (Junk)', yield: '7.85%' },
    { ticker: 'BND', name: 'Vanguard Total Bond Market Index Fund', category: 'Aggregate Market', yield: '4.35%' },
    { ticker: 'MUB', name: 'iShares National Muni Bond ETF', category: 'Municipal', yield: '3.25%' },
    { ticker: 'BNDX', name: 'Vanguard Total International Bond', category: 'International', yield: '3.65%' },
  ]);

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-8 pb-12">
       <div className="shrink-0 mb-4">
         <h1 className={`text-2xl sm:text-3xl font-black ${themeClasses.textHead} flex items-center gap-3`}><Landmark className="text-emerald-500" size={28}/> Fixed Income</h1>
       </div>

       <div className={`p-3 sm:p-4 rounded-2xl border border-indigo-500/20 bg-indigo-500/5`}>
          <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-1 flex items-center gap-2"><Info size={12}/> Holdings & Execution</h4>
          <p className={`text-[11px] sm:text-xs ${themeClasses.textMain} leading-relaxed font-medium mb-2`}>The most liquid way for retail investors to gain exposure is through Benchmark Bond ETFs purchased in a standard brokerage account. You may also purchase actual U.S. government bonds, bills, and notes directly from the source.</p>
          <a href="https://www.treasurydirect.gov/" target="_blank" rel="noreferrer" className="text-[10px] font-black uppercase text-indigo-500 tracking-widest hover:underline flex items-center gap-1"><ExternalLink size={10}/> Open TreasuryDirect</a>
       </div>

       {errorMsg && (
         <div className="p-4 rounded-xl border border-red-500/30 bg-red-500/10 flex items-center gap-3">
           <AlertCircle className="text-red-500 shrink-0" size={24} />
           <p className="text-sm font-bold text-red-400">DATA FEED INTERRUPTED: {errorMsg}</p>
         </div>
       )}

       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
           <div className={`${themeClasses.bgCard} rounded-3xl border ${themeClasses.border} shadow-xl p-4 sm:p-8 flex flex-col min-h-[250px] sm:min-h-[300px]`}>
              <h3 className={`text-[10px] font-black text-emerald-500 uppercase mb-6 tracking-widest flex justify-between items-center`}>
                U.S. Treasury Yield Curve
                {loading && <Loader2 size={12} className="animate-spin text-emerald-500" />}
              </h3>
              <div className="flex-1 w-full">
                {rates.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={rates} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" opacity={0.5} />
                      <XAxis dataKey="maturity" stroke="#475569" fontSize={10} axisLine={false} tickLine={false} />
                      <YAxis 
                        type="number" 
                        domain={[dataMin => Math.max(0, dataMin - 0.5), dataMax => dataMax + 0.5]} 
                        stroke="#475569" 
                        fontSize={10} 
                        tickFormatter={v => `${v.toFixed(1)}%`} 
                        axisLine={false} 
                        tickLine={false} 
                        width={60} 
                      />
                      <Tooltip contentStyle={{backgroundColor: '#000', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px'}} formatter={(value) => [`${value}%`, 'Yield']} />
                      <Line type="monotone" dataKey="rate" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 4 }} activeDot={{ r: 6 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                     <p className={`text-xs font-black uppercase tracking-widest ${themeClasses.textMuted}`}>Chart Unavailable</p>
                  </div>
                )}
              </div>
           </div>

           <div className={`${themeClasses.bgCard} rounded-3xl border ${themeClasses.border} shadow-xl overflow-hidden flex flex-col`}>
              <div className={`p-4 sm:p-6 border-b ${themeClasses.border} ${themeClasses.input} shrink-0`}>
                 <h3 className={`text-[10px] font-black text-emerald-500 uppercase tracking-widest`}>Raw Yield Data</h3>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar p-0">
                  <table className="w-full text-left text-sm">
                      <thead className={`sticky top-0 ${themeClasses.bgCard} z-10 shadow-sm`}>
                        <tr className={`${themeClasses.textMuted} uppercase text-[10px] tracking-widest border-b ${themeClasses.border}`}>
                          <th className="px-4 py-3 font-black">Maturity</th>
                          <th className="px-4 py-3 font-black text-right">Current Yield</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rates.length > 0 ? rates.map((t, i) => (
                          <tr key={i} className={`border-b ${themeClasses.border} hover:bg-emerald-500/5 transition-colors`}>
                            <td className={`px-4 py-3 font-black ${themeClasses.textHead}`}>{t.maturity}</td>
                            <td className={`px-4 py-3 text-right font-mono text-emerald-500 font-bold`}>{t.rate}%</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="2" className={`p-8 text-center text-xs font-black uppercase tracking-widest ${themeClasses.textMuted}`}>Data Missing</td>
                          </tr>
                        )}
                      </tbody>
                  </table>
              </div>
           </div>
       </div>

       <div className={`${themeClasses.bgCard} rounded-3xl border ${themeClasses.border} shadow-xl overflow-hidden`}>
          <div className={`p-4 sm:p-6 border-b ${themeClasses.border} ${themeClasses.input}`}>
             <h3 className={`text-[10px] font-black text-emerald-500 uppercase tracking-widest`}>Expanded Benchmark ETFs</h3>
          </div>
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left text-sm whitespace-nowrap sm:whitespace-normal">
                <thead>
                  <tr className={`${themeClasses.textMuted} uppercase text-[10px] tracking-widest border-b ${themeClasses.border}`}>
                    <th className="px-4 py-4 font-black">Ticker</th>
                    <th className="px-4 py-4 font-black">Fund Name</th>
                    <th className="px-4 py-4 font-black">Category</th>
                    <th className="px-4 py-4 font-black text-right">Trailing Yield</th>
                  </tr>
                </thead>
                <tbody>
                  {bondETFs.map((t, i) => (
                    <tr 
                      key={i} 
                      onClick={() => triggerDeepDive(t.ticker)}
                      className={`border-b ${themeClasses.border} hover:bg-emerald-500/10 cursor-pointer transition-colors group`}
                      title="Click for Deep Dive"
                    >
                      <td className={`px-4 py-4 font-black text-emerald-500 group-hover:text-emerald-400 flex items-center gap-2`}>
                        {t.ticker} <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
                      </td>
                      <td className={`px-4 py-4 font-bold ${themeClasses.textHead}`}>{t.name}</td>
                      <td className={`px-4 py-4 font-mono text-[10px] sm:text-xs ${themeClasses.textMuted} uppercase tracking-widest`}>{t.category}</td>
                      <td className={`px-4 py-4 text-right font-mono text-emerald-500 font-black`}>{t.yield}</td>
                    </tr>
                  ))}
                </tbody>
            </table>
          </div>
       </div>
    </div>
  );
}